import { FC, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FunnelDetailDto } from 'api/funnel';
import { funnelsAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikSwitch } from 'components/forms/formik/FormikSwitch';
import { ModalShell } from 'back-office/shared/components/ModalShell';
import { bankAccountLabel } from '../hooks/useBankAccountOptions';

export interface FunnelSettingsFormValues {
  designation: string;
  isActive: boolean;
  fraisResiliation: number | '';
  brokerCollectionAllowed: boolean;
  compteBancaireId: string;
}

const validationSchema = Yup.object().shape({
  designation: Yup.string()
    .trim()
    .required('La désignation est requise')
    .max(255, 'La désignation ne peut pas dépasser 255 caractères'),
  fraisResiliation: Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .min(0, 'Les frais de résiliation ne peuvent pas être négatifs')
    .nullable(),
  compteBancaireId: Yup.string().required('Le compte bancaire du bénéficiaire est requis'),
});

interface FunnelSettingsModalProps {
  funnel: FunnelDetailDto;
  onSuccess: () => void;
}

/**
 * Édition des paramètres du tunnel.
 *
 * Ne présente que les champs réellement acceptés par UpdateTunnelCommand. L'ancien
 * back-office proposait en plus le valideur, le nombre de prélèvements, les taux de
 * commission et les parties communes/privatives : ces champs ne font plus partie de la
 * surface d'écriture du back et n'étaient donc plus enregistrés.
 */
export const FunnelSettingsModal: FC<FunnelSettingsModalProps> = ({ funnel, onSuccess }) => {
  const formikRef = useRef<FormikProps<FunnelSettingsFormValues>>(null);
  const { closeDialog } = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const comptesBancaires = useMemo(
    () =>
      (funnel.beneficiaire?.listeComptesBancaires ?? []).map((compte) => ({
        key: compte.id,
        label: bankAccountLabel(compte.label, compte.iban),
      })),
    [funnel.beneficiaire]
  );

  const initialValues: FunnelSettingsFormValues = useMemo(
    () => ({
      designation: funnel.designation ?? '',
      isActive: funnel.isActive ?? false,
      fraisResiliation: funnel.fraisResiliation ?? '',
      brokerCollectionAllowed: funnel.brokerCollectionAllowed ?? false,
      compteBancaireId: funnel.beneficiaire?.compteBancaireId ?? '',
    }),
    [funnel]
  );

  const handleSubmit = async (values: FunnelSettingsFormValues) => {
    const beneficiairePersonneId = funnel.beneficiaire?.personneId;
    if (!beneficiairePersonneId) {
      toast.error("Ce tunnel n'a pas de bénéficiaire : impossible d'enregistrer.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await funnelsAPI.updateFunnel({
        updateFunnelCommand: {
          tunnelId: funnel.id,
          designation: values.designation.trim(),
          isActive: values.isActive,
          produitId: funnel.produit?.id,
          beneficiairePersonneId,
          compteBancaireId: values.compteBancaireId,
          fraisResiliation: values.fraisResiliation === '' ? null : Number(values.fraisResiliation),
          // Stored flag without any effect today (see TUN-005): passed through unchanged.
          isListingActive: funnel.isListingActive ?? false,
          brokerCollectionAllowed: values.brokerCollectionAllowed,
        },
      });

      if (response.isSuccess) {
        toast.success('Tunnel mis à jour');
        onSuccess();
        closeDialog();
      } else {
        toast.error(response.errorMessage || 'La mise à jour a échoué');
      }
    } catch (error) {
      console.error('Mise à jour du tunnel impossible', error);
      toast.error('La mise à jour a échoué');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      <ModalShell
        title="Modifier le tunnel"
        subtitle={funnel.designation ?? undefined}
        submit={{
          label: 'Sauvegarder',
          onClick: () => formikRef.current?.submitForm(),
          disabled: isSubmitting,
        }}
      >
      <Form className="flex flex-col gap-5 rounded-xl border border-gray-200 bg-white p-4">
        <FormikInputText name="designation" label="Désignation" required />

        <FormikDropdown
          name="compteBancaireId"
          label="Compte bancaire du bénéficiaire"
          required
          items={comptesBancaires}
          placeholder={
            comptesBancaires.length ? 'Sélectionner un compte' : 'Aucun compte disponible'
          }
        />

        <FormikInputText
          name="fraisResiliation"
          label="Frais de résiliation (€)"
          type="number"
          placeholder="0"
        />

        <div className="flex flex-col gap-4 rounded-xl bg-gray-50 p-4">
          <FormikSwitch name="isActive" label="Tunnel actif" />
          <FormikSwitch name="brokerCollectionAllowed" label="Encaissement par le courtier autorisé" />
          <p className="text-xs text-gray-500">
            Le courtier peut être prélevé à la place du souscripteur (courtier encaisseur) : le choix
            du payeur apparaît alors dans les conditions particulières. Le courtier règle la cotisation
            brute, sa commission est reversée comme d'habitude.
          </p>
        </div>
      </Form>
      </ModalShell>
    </Formik>
  );
};
